import React from 'react';

import { T } from '../customer/customers.theme';
import { COPY } from './settings.copy';
import { MAX_UNIT_LENGTH } from './settings.rules';
import {
  Card,
  EmptyNote,
  Field,
  FieldError,
  PlusGlyph,
  SectionHead,
  SmallButton,
  TYPE,
} from './SettingsUI';

/**
 * The units a stock item can be measured in.
 *
 * Laid out as a wrapping row of small boxes rather than a column of full-width
 * rows, because a unit is two or three characters and a restaurant has a dozen
 * of them: as rows they would run off the bottom of the screen and look like a
 * much bigger setting than they are.
 *
 * Each box carries its own × rather than a bin beside it, for the same reason
 * — at this size a separate button would be wider than the field it removes.
 */

const Remove = ({ onClick, disabled, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 22,
      height: 22,
      padding: 0,
      marginRight: -4,
      background: 'transparent',
      border: 'none',
      borderRadius: T.radiusPill,
      color: T.inkFaint,
      cursor: disabled ? 'not-allowed' : 'pointer',
      flexShrink: 0,
    }}
  >
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  </button>
);

export default function UnitsCard({
  rows,
  errors,
  showErrors,
  busy,
  full,
  onChange,
  onAdd,
  onRemove,
}) {
  const problems = Object.keys(errors)
    .filter((k) => k !== 'all')
    .map((k) => errors[k]);

  return (
    <Card>
      <SectionHead title={COPY.unitsTitle} body={COPY.unitsBody} />

      {rows.length ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {rows.map((row, index) => (
            <div key={row.key} style={{ width: 150 }}>
              <Field
                name={`settings-unit-${index}`}
                value={row.value}
                onChange={(value) => onChange(index, { value })}
                placeholder={COPY.unitPlaceholder}
                maxLength={MAX_UNIT_LENGTH}
                disabled={busy}
                invalid={showErrors && Boolean(errors[index])}
                trailing={
                  <Remove
                    onClick={() => onRemove(index)}
                    disabled={busy}
                    label={`${COPY.remove} ${row.value || index + 1}`}
                  />
                }
              />
            </div>
          ))}
        </div>
      ) : (
        <EmptyNote>{COPY.unitsEmpty}</EmptyNote>
      )}

      {showErrors ? <FieldError>{errors.all || problems[0] || ''}</FieldError> : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginTop: 16,
          flexWrap: 'wrap',
        }}
      >
        <SmallButton onClick={onAdd} disabled={busy || full} icon={<PlusGlyph />}>
          {COPY.unitAdd}
        </SmallButton>
        {full ? (
          <span style={{ fontSize: TYPE.hint, color: T.inkFaint }}>{COPY.unitsFull}</span>
        ) : null}
      </div>
    </Card>
  );
}
